'use client'

/**
 * PageError — route-level `error.tsx` boundary UI.
 *
 * Renders the canonical `ErrorCard` (DES-50 rung 1 — the atom already exists;
 * PageError composes it with page-scale spacing rather than hand-rolling its
 * own `<h2>` + `<p>` + `<Button>`, which is what this file did before). This
 * keeps the page-level and section-level error surfaces visually coherent —
 * both now go through `ErrorCard`, `variant="card"` — mirroring
 * `SectionErrorFallback` (`section-error-boundary.tsx`).
 *
 * Reports to Sentry with route + project attribution (REL-25), via the
 * exported `capturePageError()` — same `Sentry.withScope` shape
 * `SectionErrorFallback` uses for section + project (`with-auth.ts`'s
 * route/method/userId `withScope` is the shape both copy).
 *
 * §6 of .claude/skills/dashboard-design/loading-error-states.md, ARC-06.
 *
 * WHEN: every `error.tsx` under `(main)/app/**` and `(main)/onboarding/**`.
 *   'use client'
 *   import { PageError } from '@/components/shared/page-error'
 *   export default function XError({
 *     error,
 *     unstable_retry,
 *   }: {
 *     error: Error & { digest?: string }
 *     unstable_retry: () => void
 *   }) {
 *     return (
 *       <PageError
 *         error={error}
 *         unstable_retry={unstable_retry}
 *         title="Couldn't load your X"
 *         message="Try again — <what's unaffected, or what to expect>."
 *       />
 *     )
 *   }
 *
 * NEVER: pass a generic `title` like "Something went wrong" — name the
 *   surface that failed (REL-26, DES-44). NEVER let `message` restate
 *   `title` — it should read as the remedy, not a repeat of the headline.
 *   NEVER call bare `reset()` instead of `unstable_retry()` (DES-25) —
 *   `reset()` only re-renders the same failed cached data and never actually
 *   recovers. NEVER use as a substitute for `SectionErrorBoundary` inside a
 *   page — this is for a whole route failing, not one data region.
 */

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import * as Sentry from '@sentry/nextjs'
import { ErrorCard } from '@/components/shared/error-card'
import {
  SKEW_DESCRIPTION,
  SKEW_TITLE,
  useSkewAwareError,
} from '@/lib/observability/use-skew-aware-error'
import { useProjectOptional } from '@/components/providers/project-provider'

interface PageErrorProps {
  error: Error & { digest?: string }
  /**
   * `unstable_retry` re-fetches and re-renders the segment (preferred).
   * Bare `reset` only re-renders without re-fetching, so failed data never
   * recovers. Pass the `unstable_retry` prop from the error.tsx boundary.
   */
  unstable_retry: () => void
  /**
   * Names the surface that failed — e.g. "Couldn't load your competitors".
   * No generic default for a new call site (REL-26, DES-44). Optional only
   * so the ~14 call sites outside `(main)/app/**` — onboarding, marketing,
   * auth, invite, report, unsubscribe — keep compiling unchanged; G15's
   * scope is the 16 `(main)/app/**` route segments, which all pass one.
   * Give any new caller a specific title, not this fallback.
   */
  title?: string
  /** The remedy sentence shown under the title. Should not restate `title`. */
  message: string
}

/** Fallback for callers outside G15's scope that have not been given a specific title yet. */
const DEFAULT_TITLE = 'Something went wrong'

/** Longest the retry button stays pending when the boundary never re-renders a new error. */
const RETRY_PENDING_MAX_MS = 15_000

interface PageErrorAttribution {
  route: string
  projectId: string | null
}

/**
 * Reports a page-level `error.tsx` catch to Sentry with route + project
 * attribution (REL-25). Exported so `__tests__/page-error.test.tsx` can
 * assert on the captured scope directly — this repo has no jsdom, so a
 * `useEffect` can't be exercised through `renderToStaticMarkup`
 * (`section-error-boundary.test.tsx`'s docblock explains the same gap for
 * `SectionErrorFallback`).
 */
export function capturePageError(
  error: Error & { digest?: string },
  attribution: PageErrorAttribution
): void {
  Sentry.withScope((scope) => {
    scope.setTag('route', attribution.route)
    scope.setContext('page_error', {
      route: attribution.route,
      projectId: attribution.projectId,
      digest: error.digest ?? null,
    })
    Sentry.captureException(error)
  })
}

export function PageError({
  error,
  unstable_retry,
  title = DEFAULT_TITLE,
  message,
}: PageErrorProps) {
  // `useProjectOptional()` never throws — an error.tsx can catch an error
  // thrown by the layout itself, in which case ProjectProvider may not be
  // mounted, so the throwing `useProject()` would crash inside the boundary.
  const project = useProjectOptional()
  const pathname = usePathname()

  // Latest-ref pattern, ref written from an effect (not during render — see
  // react-hooks/refs): a separate sync effect keeps the ref current on every
  // pathname/project change, while the capture effect below depends on
  // `[error]` only, so a re-render that changes `project`/`pathname` without
  // a *new* error never re-fires the Sentry capture.
  const attributionRef = useRef<PageErrorAttribution>({
    route: pathname,
    projectId: project?.currentProject.id ?? null,
  })

  useEffect(() => {
    attributionRef.current = {
      route: pathname,
      projectId: project?.currentProject.id ?? null,
    }
  }, [pathname, project])

  // Skew (and its after-reload recurrence) is reported by the hook; only ordinary errors are
  // captured here, so one error is never sent twice.
  const { kind, isSkew, onReload } = useSkewAwareError(error, pathname)

  useEffect(() => {
    if (kind === 'ordinary') capturePageError(error, attributionRef.current)
  }, [error, kind])

  // `unstable_retry` is a multi-second server refetch with no UI of its own, so the click
  // read as dead. The button stays pending from the click until the boundary renders a
  // *different* error (a failed retry) or unmounts (success). Keyed to the error object, so
  // a new error re-enables it with no effect to reset state.
  const [retriedFor, setRetriedFor] = useState<Error | null>(null)
  // Bounded fallback: a retry that rethrows the *same* Error instance may keep this fallback
  // mounted with an unchanged object, which would leave the button pending forever.
  const [timedOutFor, setTimedOutFor] = useState<Error | null>(null)
  useEffect(() => {
    if (retriedFor === null) return
    const timer = setTimeout(() => setTimedOutFor(retriedFor), RETRY_PENDING_MAX_MS)
    return () => clearTimeout(timer)
  }, [retriedFor])
  const retryPending = retriedFor === error && timedOutFor !== error
  const withPending = (action: () => void) => () => {
    setRetriedFor(error)
    action()
  }

  if (isSkew) {
    // A full reload is the only remedy: `unstable_retry` re-renders on the same stale client
    // bundle, which still holds the dead Server Action id.
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <ErrorCard
          variant="card"
          title={SKEW_TITLE}
          description={SKEW_DESCRIPTION}
          onRetry={withPending(onReload)}
          retryLabel="Reload"
          retryPending={retryPending}
          className="w-full max-w-md"
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-24">
      <ErrorCard
        variant="card"
        title={title}
        description={message}
        onRetry={withPending(unstable_retry)}
        retryLabel="Try again"
        retryPending={retryPending}
        className="w-full max-w-md"
      />
    </div>
  )
}
